import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import type { AnnouncementLevel, AnnouncementPattern } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * お知らせ管理 hook (admin 第 15 弾、Phase 5 REQ-4.1.25 / 4.3.33)。
 *
 * 旧 `old/apps/admin/src/pages/AnnouncementsPage.tsx` の write フローを minimum scope で
 * 踏襲。本 hook は subscribe + create + update (archive 等) + 物理 delete helper を提供。
 *
 * 認可: firestore.rules `isDistrictAdmin(districtId)` → master / district admin (担当地区) のみ
 * write 可能。user は read のみ。
 *
 * 旧との差分:
 *   - subscribe 順序を `createdAt desc` → `startAt desc` に変更 (旧 user lib announcements 互換、
 *     配信期間順で並べたほうが admin 一覧として直観的)
 *   - field 名: `display` → `pattern` (docs/04 §4-3 で `pattern` 確定、旧 user lib の
 *     `display ?? pattern` 後方互換は v2 では廃止 = 旧 migration スクリプト側で rename 想定)
 *   - 物理 delete も追加 (旧は archive のみ、v2 admin は誤投稿の取消対応で物理 delete も許容)
 */

export type AnnouncementsState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: AnnouncementListItem[]; error: null }
  | { status: 'error'; items: []; error: Error };

/** 一覧表示用 narrow 型 (createdAt 等は admin 表示に最低限のみ保持)。 */
export interface AnnouncementListItem {
  id: string;
  title: string;
  body: string;
  level: AnnouncementLevel;
  pattern: AnnouncementPattern;
  startAt: Date | null;
  endAt: Date | null;
  archivedAt: Date | null;
  createdAt: Date | null;
  targetSessionIds: string[];
  forceReshow: boolean;
}

function tsToDate(value: unknown): Date | null {
  if (!value) return null;
  if (typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as { toDate(): Date }).toDate();
    } catch {
      return null;
    }
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function mapDoc(snap: { id: string; data: () => DocumentData }): AnnouncementListItem {
  const data = snap.data();
  return {
    id: snap.id,
    title: typeof data.title === 'string' ? data.title : '',
    body: typeof data.body === 'string' ? data.body : '',
    level: (data.level as AnnouncementLevel) ?? 'info',
    pattern: (data.pattern as AnnouncementPattern) ?? 'banner',
    startAt: tsToDate(data.startAt),
    endAt: tsToDate(data.endAt),
    archivedAt: tsToDate(data.archivedAt),
    createdAt: tsToDate(data.createdAt),
    targetSessionIds: Array.isArray(data.targetSessionIds)
      ? (data.targetSessionIds as string[])
      : [],
    forceReshow: data.forceReshow === true,
  };
}

export function useAnnouncements(districtId: string | null): AnnouncementsState {
  const [state, setState] = useState<AnnouncementsState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({ status: 'error', items: [], error: new Error('districtId is required') });
      return;
    }
    const q = query(
      collection(db, 'districts', districtId, 'announcements'),
      orderBy('startAt', 'desc'),
    );
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map(mapDoc);
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [districtId]);

  return state;
}

/** create input (UI 側で必須/optional を validation 済み前提)。 */
export interface CreateAnnouncementInput {
  title: string;
  body: string;
  level: AnnouncementLevel;
  pattern: AnnouncementPattern;
  /** ISO8601、null = 即時 */
  startAt: string | null;
  /** ISO8601、null = 無期限 */
  endAt: string | null;
  targetSessionIds?: string[];
  forceReshow?: boolean;
}

export async function createAnnouncement(
  districtId: string,
  input: CreateAnnouncementInput,
  uid: string,
): Promise<string> {
  const ref = collection(db, 'districts', districtId, 'announcements');
  const doc = await addDoc(ref, {
    schemaVersion: 1,
    title: input.title.trim(),
    body: input.body.trim(),
    level: input.level,
    pattern: input.pattern,
    startAt: input.startAt,
    endAt: input.endAt,
    archivedAt: null,
    targetSessionIds: input.targetSessionIds ?? [],
    forceReshow: input.forceReshow === true,
    // serverTimestamp は型上 FieldValue で Announcement.createdAt: string と乖離するが、
    // Firestore 側で write 時に Timestamp に解決される (admin / user 側 read で
    // string 化されるため shared-types 型は維持)
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: uid,
    updatedBy: uid,
  });
  return doc.id;
}

/**
 * archivedAt を設定して配信終了 (旧 v1 の archive ボタンと同 logic)。
 * 物理 delete は別関数 `deleteAnnouncement` で提供。
 */
export async function archiveAnnouncement(
  districtId: string,
  announcementId: string,
  uid: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'announcements', announcementId);
  await setDoc(
    ref,
    { archivedAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedBy: uid },
    { merge: true },
  );
}

export async function deleteAnnouncement(
  districtId: string,
  announcementId: string,
): Promise<void> {
  const ref = doc(db, 'districts', districtId, 'announcements', announcementId);
  await deleteDoc(ref);
}
